import { ReactNode, useState, useEffect, useRef } from "react";
import { useSession } from "@/hooks/use-session";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useAnalytics } from "@/hooks/use-analytics";
import { Session } from "@supabase/supabase-js";
import { PricingModal } from "@/components/subscription/PricingModal";
import { 
  SubscriptionContextType,
  PricingModalState, 
  TimeSensitiveOffer 
} from "./subscription-types";
import { SubscriptionContext } from "./subscription-context";
import { safeLocalStorage } from "@/utils/localStorage";

// Constants for localStorage keys
const USAGE_DATA_KEY = 'strykr_usage_data';
const SUBSCRIPTION_DATA_KEY = 'strykr_subscription_data';
const MODAL_TIMESTAMP_KEY = 'strykr_modal_last_shown';
const PAYWALL_SESSION_KEY = 'strykr_paywall_session';

// Type definitions for localStorage data structures
interface UsageData {
  queryCount: number;
  lastResetDate?: string; // Optional because it might not exist in older saved data
}

interface SubscriptionData {
  subscription_status?: string;
  trial_end_date?: string;
  current_period_end?: string;
}

interface PaywallSessionData {
  hits: Array<{ feature: string; timestamp: number }>;
  timestamp: number;
  lastModalShown: number;
}

/**
 * Provider component that makes subscription state available to any child component
 */
export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  // Get the current user session
  const { session } = useSession();
  const analytics = useAnalytics();
  
  // State for pricing modal
  const [modalState, setModalState] = useState<PricingModalState>({
    isPricingOpen: false,
    source: '',
    timeSensitiveOffer: null
  });
  
  // Use our enhanced subscription hook with Edge Function integration
  const subscriptionData = useSubscriptionStatus(session);
  
  // Log the Edge Function response for debugging - only when values change
  const prevSubscriptionDataRef = useRef({ 
    isSubscribed: subscriptionData.isSubscribed, 
    isTrialing: subscriptionData.isTrialing,
    allowedDateRange: subscriptionData.allowedDateRange
  });
  
  useEffect(() => {
    const prevData = prevSubscriptionDataRef.current;
    const hasChanged = 
      prevData.isSubscribed !== subscriptionData.isSubscribed ||
      prevData.isTrialing !== subscriptionData.isTrialing ||
      JSON.stringify(prevData.allowedDateRange) !== JSON.stringify(subscriptionData.allowedDateRange);
    
    if (hasChanged) {
      console.log('[SubscriptionContext] Edge Function data:', { 
        isSubscribed: subscriptionData.isSubscribed, 
        isTrialing: subscriptionData.isTrialing,
        trial_date_range: subscriptionData.allowedDateRange
      });
      prevSubscriptionDataRef.current = {
        isSubscribed: subscriptionData.isSubscribed, 
        isTrialing: subscriptionData.isTrialing,
        allowedDateRange: subscriptionData.allowedDateRange
      };
    }
  }, [subscriptionData.isSubscribed, subscriptionData.isTrialing, subscriptionData.allowedDateRange]);

  // Use the subscription data directly from the Edge Function
  // No more overrides - trust the Edge Function response
  const isSubscribed = subscriptionData.isSubscribed;
  const isTrialing = subscriptionData.isTrialing;
  const isLoading = subscriptionData.isLoading;
  const checkSubscriptionStatus = subscriptionData.checkSubscriptionStatus;

  // --- BEGIN: Usage Limit State Management ---
  const [queryCount, setQueryCount] = useState<number>(0);
  // Free users get 5 queries per day, matching FREE_TIER_QUERY_LIMIT in use-query-limits.ts
  const queryLimit = isSubscribed ? Infinity : (isTrialing ? 250 : 5);
  const calendarDaysLimit = isSubscribed || isTrialing ? Infinity : 2;

  // Load usage data from localStorage on mount
  useEffect(() => {
    const usageData = safeLocalStorage.get<UsageData>(USAGE_DATA_KEY, { queryCount: 0 });
    
    // Reset query count if it's a new day
    const today = new Date().toDateString();
    if (usageData.lastResetDate !== today) {
      safeLocalStorage.update<UsageData>(USAGE_DATA_KEY, (prevData) => ({
        ...prevData,
        queryCount: 0,
        lastResetDate: today
      }), {
        queryCount: 0,
        lastResetDate: today
      });
    } else {
      // Use existing count
      setQueryCount(usageData.queryCount || 0);
    }
    
    // Listen for custom events from DevControls
    const handleQueryLimitUpdate = (event: CustomEvent) => {
      if (event.detail && typeof event.detail.queryCount === 'number') {
        console.log('📊 Received query limit update:', event.detail);
        setQueryCount(event.detail.queryCount);
      }
    };
    
    const handleForceCheckLimits = () => {
      console.log('🔄 Force checking subscription limits');
      const currentUsageData = safeLocalStorage.get<UsageData>(USAGE_DATA_KEY, { queryCount: 0 });
      setQueryCount(currentUsageData.queryCount || 0);
    };
    
    // Add event listeners
    window.addEventListener('strykr:query-limit-update', handleQueryLimitUpdate as EventListener);
    window.addEventListener('strykr:force-check-limits', handleForceCheckLimits);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('strykr:query-limit-update', handleQueryLimitUpdate as EventListener);
      window.removeEventListener('strykr:force-check-limits', handleForceCheckLimits);
    };
  }, []); // Run on mount

  // Function to increment query count
  const incrementQueryCount = (): number => {
    const newCount = queryCount + 1;
    setQueryCount(newCount);
    
    // Update localStorage with new count
    safeLocalStorage.update<UsageData>(USAGE_DATA_KEY, (prevData) => ({
      ...prevData,
      queryCount: newCount,
      lastResetDate: prevData.lastResetDate || new Date().toDateString()
    }), { queryCount: newCount, lastResetDate: new Date().toDateString() });
    
    return newCount;
  };
  // --- END: Usage Limit State Management ---

  // Log subscription state for debugging - only when values change
  const prevStateRef = useRef({ isSubscribed, isLoading, isTrialing, queryCount, queryLimit });
  useEffect(() => {
    const prevState = prevStateRef.current;
    const hasChanged = 
      prevState.isSubscribed !== isSubscribed ||
      prevState.isLoading !== isLoading ||
      prevState.isTrialing !== isTrialing ||
      prevState.queryCount !== queryCount ||
      prevState.queryLimit !== queryLimit;
    
    if (hasChanged) {
      console.log('🔔 SubscriptionContext state:', { isSubscribed, isLoading, isTrialing, queryCount, queryLimit, calendarDaysLimit });
      prevStateRef.current = { isSubscribed, isLoading, isTrialing, queryCount, queryLimit };
    }
  }, [isSubscribed, isLoading, isTrialing, queryCount, queryLimit, calendarDaysLimit]);
  
  // Add hasSeenModalRecently function
  const hasSeenModalRecently = (): boolean => {
    try {
      const lastShownTimestamp = safeLocalStorage.get<{ timestamp: number } | null>(MODAL_TIMESTAMP_KEY, null);
      
      if (!lastShownTimestamp) return false;
      
      const now = new Date().getTime();
      const thirtyMinutesMs = 30 * 60 * 1000;
      
      return now - lastShownTimestamp.timestamp < thirtyMinutesMs;
    } catch (e) {
      return false;
    }
  };

  // Add time-sensitive offer function
  const getTimeSensitiveOffer = (): TimeSensitiveOffer | null => {
    // For trial users with 1 or fewer days left
    if (isTrialing) {
      return { discount: '20%', expiresIn: '24 hours', message: 'Last chance! Your trial ends soon.' };
    } else if (!isTrialing && !isSubscribed) {
      return { discount: '15%', expiresIn: '48 hours', message: 'Limited-time offer!' };
    }
    return null;
  };

  // Open pricing modal function
  const openPricingModal = (source = 'general'): void => {
    safeLocalStorage.set(MODAL_TIMESTAMP_KEY, { timestamp: new Date().getTime() });
    
    analytics.trackEvent('pricing_modal_opened', { source });
    
    setModalState({
      isPricingOpen: true,
      source,
      timeSensitiveOffer: getTimeSensitiveOffer()
    });
  };

  // Add tracking with cooldown for paywall hits
  const trackPaywallHit = (feature: string): void => {
    if (isSubscribed) return;
    
    // Use our existing analytics hook
    analytics.trackEvent('paywall_hit', { feature });
    
    // Implement the session storage with auto-trigger logic
    const now = new Date().getTime();
    let session = safeLocalStorage.get<PaywallSessionData>(PAYWALL_SESSION_KEY, {
      hits: [],
      timestamp: now,
      lastModalShown: 0
    });
    
    // Initialize or validate session
    if (!session.hits || !session.timestamp || now - session.timestamp > 86400000) { // 24 hours
      session = { hits: [], timestamp: now, lastModalShown: 0 };
    }
    
    session.hits.push({ feature, timestamp: now });
    
    // Auto-trigger if conditions met (2+ hits and not on cooldown)
    if (session.hits.length >= 2 && now - session.lastModalShown >= 3600000) { // 1 hour cooldown
      session.lastModalShown = now;
      safeLocalStorage.set(PAYWALL_SESSION_KEY, session);
      
      // Only trigger if we haven't shown a modal recently
      if (!hasSeenModalRecently()) {
        setTimeout(() => {
          openPricingModal('auto_trigger');
          analytics.trackEvent('pricing_modal_auto_triggered', {
            trigger_hit_count: session.hits.length,
            latest_feature: feature
          });
        }, 500); // Small delay for better UX
      }
    } else {
      safeLocalStorage.set(PAYWALL_SESSION_KEY, session);
    }
  };
  
  const contextValue: SubscriptionContextType = {
    isSubscribed,
    isLoading,
    isTrialing,
    checkSubscription: checkSubscriptionStatus,
    // New functions
    hasSeenModalRecently,
    getTimeSensitiveOffer,
    trackPaywallHit,
    openPricingModal,
    incrementQueryCount,
    // Modal state
    modalState,
    setModalState,
    // Usage Limits
    queryCount,
    queryLimit,
    calendarDaysLimit,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
      {modalState.isPricingOpen && (
        <PricingModal
          open={modalState.isPricingOpen}
          onOpenChange={(open) => setModalState(prev => ({ ...prev, isPricingOpen: open }))}
          source={modalState.source}
          offer={modalState.timeSensitiveOffer}
        />
      )}
    </SubscriptionContext.Provider>
  );
};

// Context hook moved to a separate file (use-subscription.ts) to help with fast refresh